<script setup>
import _ from 'lodash';
import { ref, watch } from 'vue';
import api from '../../api.js';
import { formatTimestamp } from '../../helpers';

const props = defineProps({
    modelValue: String
});
const searchContentstreams = ref([]);
const searchIsLoading = ref(false);
const searchPage = ref(1);
const debounceSearch = _.debounce(search, 300);
watch(
    () => props.modelValue,
    (current) => {
        // NOTE: Delayed search to avoid multiple requests
        searchPage.value = 1;
        debounceSearch(current);
    }
);

watch(
    () => searchPage.value,
    () => {
        search(props.modelValue);
    }
);

async function search(value) {
    if (value) {
        searchIsLoading.value = true;
        searchContentstreams.value = [];

        await api.get(`/api/v1/contentstream-types`).then((response) => {
            const types = ['dsu', 'dmu', 'wmu'];
            const typeIds = response.data.data.filter((contentType) => types.includes(contentType.name));
            const typeConfig = [];

            typeIds.forEach((type) => {
                typeConfig.push(type.id);
            });

            api.get(`/api/v1/contentstreams`, {
                params: {
                    page: searchPage.value,
                    page_size: 9,
                    search_phrase: value.toLowerCase(),
                    key: 'market_search',
                    contentstream_type_id: typeConfig,
                    'sort[by]': 'published_at',
                    'sort[order]': 'desc'
                }
            })
                .then((response) => {
                    searchIsLoading.value = false;
                    searchContentstreams.value = response.data;
                })
                .catch(() => {
                    searchIsLoading.value = false;
                    searchContentstreams.value = [];
                });
        });
    } else {
        searchIsLoading.value = false;
        searchContentstreams.value = [];
    }
}
</script>

<template>
    <div v-if="searchIsLoading">
        <div class="w-full h-1 bg-[#F1F6FA] rounded-xl my-12"></div>
        <div class="p-2 text-center text-slate-400">{{ $t('search_markets') }}...</div>
    </div>

    <div v-if="searchContentstreams && searchContentstreams?.data?.length > 0">
        <div class="w-full h-1 bg-[#F1F6FA] rounded-xl my-12"></div>

        <!-- Search Results -->
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-4 justify-items-center p-4">
            <div
                v-for="searchContent in searchContentstreams.data"
                :key="searchContent.id"
                class="box h-auto w-full max-w-[300px] justify-self-center group relative fade-in border-0 flex flex-col bg-white rounded-xl shadow-md hover:shadow-lg hover:scale-105 transition-all"
            >
                <router-link :to="'/dashboard/markets-analysis/' + searchContent.id" class="contents">
                    <div class="rounded-t-xl flex justify-center bg-[#0000000d] duration-300 backdrop-blur-md">
                        <div class="text-sm p-4">{{ formatTimestamp(searchContent.published_at) }}</div>
                    </div>

                    <div class="flex flex-col items-center justify-center px-4 py-4 text-center">
                        <strong class="text-base font-semibold mb-2">
                            {{ $translate(searchContent.title) }}
                        </strong>
                        <small class="block text-xs text-gray-500">
                            {{ $translate(searchContent.contentstreamType.title) }}
                        </small>
                        <div class="flex flex-wrap gap-2 my-4 select-none">
                            <div v-for="logo in searchContent?.logos" :key="logo.id">
                                <img v-if="logo?.url" :src="logo?.url" :alt="logo.name" class="w-8 h-8 rounded-full" />
                            </div>
                        </div>
                    </div>
                </router-link>
            </div>
        </div>

        <!-- Pagination -->
        <div class="my-12 space-y-3 select-none">
            <div class="flex flex-wrap justify-center gap-1">
                <div v-for="page in searchContentstreams.meta.links.slice(1, searchContentstreams.meta.links.length - 1)" class="cursor-pointer">
                    <div v-if="searchContentstreams.meta.current_page == page.label" class="rounded-xl bg-blue text-white">
                        <div class="w-10 h-10 text-center flex items-center justify-center">
                            <span v-html="page.label"></span>
                        </div>
                    </div>
                    <div v-else class="rounded-xl hover:bg-slate-100" @click="searchPage = page.label">
                        <div class="w-10 h-10 text-center flex items-center justify-center">
                            <span v-html="page.label"></span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="space-x-2 flex flex-wrap items-center justify-center text-xxs text-slate-500">
                <span>{{ $t('plain_shows') }}</span>
                <span>{{ searchContentstreams.meta.per_page }}</span>
                <span>{{ $t('plain_of') }}</span>
                <span>{{ searchContentstreams.meta.total }}</span>
                <span>{{ $t('plain_results') }}</span>
            </div>
        </div>
    </div>
</template>
